import { useEffect, useRef, useState } from 'react';
import { API_BASE, setupAPI } from '../../api/client';
import type { QREvent } from '../../types';
import { QRCountdown } from './QRCountdown';

type QRState = 'loading' | 'showing' | 'connected' | 'timeout' | 'error';

interface ConnectWhatsAppStepProps {
  whatsappConnected: boolean;
  onConnected: () => void;
  onNext: () => void;
  onSkip: () => void;
}

export function ConnectWhatsAppStep({
  whatsappConnected,
  onConnected,
  onNext,
  onSkip,
}: ConnectWhatsAppStepProps) {
  const [qrState, setQrState] = useState<QRState>(
    whatsappConnected ? 'connected' : 'loading'
  );
  const [qrData, setQrData] = useState<QREvent | null>(null);
  const [restarting, setRestarting] = useState(false);
  const esRef = useRef<EventSource | null>(null);

  // Start SSE stream for QR codes
  useEffect(() => {
    if (whatsappConnected) {
      setQrState('connected');
      return;
    }

    function connectSSE() {
      const es = new EventSource(`${API_BASE}/api/setup/qr-stream`);
      esRef.current = es;

      es.addEventListener('qr', (e: MessageEvent) => {
        try {
          const data: QREvent = JSON.parse(e.data);
          setQrData(data);
          setQrState('showing');
        } catch {
          // Fallback for raw base64 format
          setQrData({ image: e.data, ttl: 20, index: 1, total: 6 });
          setQrState('showing');
        }
      });

      es.addEventListener('connected', () => {
        es.close();
        setQrState('connected');
        onConnected();
        setTimeout(onNext, 800);
      });

      es.addEventListener('qr_timeout', () => {
        es.close();
        setQrState('timeout');
      });

      es.addEventListener('ping', () => {
        // keep-alive
      });

      es.onerror = () => {
        es.close();
        setQrState('error');
        // Auto-retry after 3s
        setTimeout(connectSSE, 3000);
      };
    }

    connectSSE();

    return () => {
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
    };
  }, [whatsappConnected, onConnected, onNext]);

  async function handleRestart() {
    setRestarting(true);
    setQrState('loading');
    setQrData(null);
    try {
      await setupAPI.restartQR();
    } catch {
      // continue anyway
    }
    // Wait for backend to start new session, then reconnect SSE
    setTimeout(() => {
      setRestarting(false);
      if (esRef.current) esRef.current.close();
      const es = new EventSource(`${API_BASE}/api/setup/qr-stream`);
      esRef.current = es;

      es.addEventListener('qr', (e: MessageEvent) => {
        try {
          const data: QREvent = JSON.parse(e.data);
          setQrData(data);
          setQrState('showing');
        } catch {
          setQrData({ image: e.data, ttl: 20, index: 1, total: 6 });
          setQrState('showing');
        }
      });
      es.addEventListener('connected', () => {
        es.close();
        setQrState('connected');
        onConnected();
        setTimeout(onNext, 800);
      });
      es.addEventListener('qr_timeout', () => {
        es.close();
        setQrState('timeout');
      });
      es.addEventListener('ping', () => {});
      es.onerror = () => {
        es.close();
        setQrState('error');
      };
    }, 2000);
  }

  // Already connected state
  if (qrState === 'connected' || whatsappConnected) {
    return (
      <div>
        <p className="text-xs font-medium text-ink-tertiary uppercase tracking-wider mb-1" data-ph-no-mask>
          Step 1 of 4
        </p>
        <h1 className="text-2xl font-bold text-ink mb-1" data-ph-no-mask>Connect WhatsApp</h1>
        <p className="text-sm text-ink-secondary mb-6 leading-relaxed" data-ph-no-mask>
          Link your WhatsApp account to get started.
        </p>

        <div className="flex flex-col items-center py-8">
          <div className="w-16 h-16 rounded-full bg-success-bg flex items-center justify-center mb-3">
            <span className="material-icons-round text-success text-3xl">check_circle</span>
          </div>
          <p className="text-lg font-semibold text-success" data-ph-no-mask>Already connected</p>
          <p className="text-sm text-ink-secondary mt-1" data-ph-no-mask>Your WhatsApp account is linked and ready.</p>
        </div>

        <div className="flex justify-end mt-6">
          <button
            onClick={onNext}
            className="px-5 py-2.5 rounded-lg bg-accent text-white text-sm font-medium hover:bg-accent-deep transition-colors"
            data-ph-no-mask
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <p className="text-xs font-medium text-ink-tertiary uppercase tracking-wider mb-1" data-ph-no-mask>
        Step 1 of 4
      </p>
      <h1 className="text-2xl font-bold text-ink mb-1" data-ph-no-mask>Connect WhatsApp</h1>
      <p className="text-sm text-ink-secondary mb-6 leading-relaxed" data-ph-no-mask>
        Open WhatsApp on your phone, go to{' '}
        <strong className="text-ink">Settings &rarr; Linked Devices &rarr; Link a Device</strong>{' '}
        and scan the QR code below.
      </p>

      <div className="flex flex-col items-center">
        {/* Loading state */}
        {qrState === 'loading' && (
          <div className="py-12 flex flex-col items-center gap-3">
            <span className="material-icons-round text-accent text-3xl animate-spin">sync</span>
            <p className="text-sm text-ink-secondary" data-ph-no-mask>
              {restarting ? 'Requesting new QR code...' : 'Generating QR code...'}
            </p>
          </div>
        )}

        {/* QR code with countdown */}
        {qrState === 'showing' && qrData && (
          <QRCountdown
            image={qrData.image}
            ttl={qrData.ttl}
            index={qrData.index}
            total={qrData.total}
          />
        )}

        {/* Timeout state */}
        {qrState === 'timeout' && (
          <div className="py-8 flex flex-col items-center gap-3 text-center">
            <div className="w-14 h-14 rounded-full bg-error-bg flex items-center justify-center">
              <span className="material-icons-round text-error text-2xl">timer_off</span>
            </div>
            <p className="text-sm font-semibold text-error" data-ph-no-mask>QR code session expired</p>
            <p className="text-xs text-ink-secondary max-w-xs" data-ph-no-mask>
              All QR codes have expired. This can happen if you didn&apos;t scan in time.
            </p>
          </div>
        )}

        {/* Error state */}
        {qrState === 'error' && (
          <div className="py-8 flex flex-col items-center gap-3 text-center">
            <span className="material-icons-round text-warning text-3xl">wifi_off</span>
            <p className="text-sm text-ink-secondary" data-ph-no-mask>Connection lost. Retrying...</p>
          </div>
        )}
      </div>

      {qrState === 'showing' && (
        <p className="text-xs text-ink-tertiary text-center mt-4" data-ph-no-mask>
          Waiting for scan...
        </p>
      )}

      <div className="flex justify-center mt-3">
        <button
          onClick={handleRestart}
          disabled={restarting}
          className="flex items-center gap-1.5 text-xs text-ink-tertiary hover:text-accent transition-colors"
          data-ph-no-mask
        >
          <span className="material-icons-round text-sm">refresh</span>
          {restarting ? 'Refreshing...' : 'Get new QR code'}
        </button>
      </div>

      <div className="flex justify-between items-center mt-6">
        <button
          onClick={onSkip}
          className="text-sm text-ink-tertiary hover:text-ink-secondary transition-colors underline underline-offset-2"
          data-ph-no-mask
        >
          Skip for now
        </button>
        <span className="text-xs text-ink-tertiary">
          You can connect WhatsApp later in Settings
        </span>
      </div>
    </div>
  );
}
